import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  DollarSign,
  CreditCard,
  Loader2,
  CheckCircle,
  User,
  Mail,
  Phone,
  Hash
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PlayerJoin = () => {
  const { inviteCode } = useParams();
  const [teamInfo, setTeamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    player_name: '',
    player_email: '',
    parent_name: '',
    parent_email: '',
    jersey_number: ''
  });

  useEffect(() => {
    fetchTeamInfo();
  }, [inviteCode]);

  const fetchTeamInfo = async () => {
    try {
      const response = await axios.get(`${API}/join/${inviteCode}`);
      setTeamInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid invite link');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.player_name || !formData.player_email) {
      toast.error('Please fill in player name and email');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/payments/player-registration`, {
        invite_code: inviteCode,
        player_name: formData.player_name,
        player_email: formData.player_email,
        parent_name: formData.parent_name || null,
        parent_email: formData.parent_email || null,
        jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process registration');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <Users className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Invalid Invite Link</h1>
        <p className="text-muted-foreground text-center">{error}</p>
      </div>
    );
  }

  const spotsRemaining = teamInfo.max_players - teamInfo.players_paid;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-12 px-4" data-testid="player-join-page">
      <div className="max-w-lg mx-auto">
        {/* Header Card */}
        <Card className="mb-6">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Join {teamInfo.team_name}</CardTitle>
            <CardDescription>{teamInfo.event_name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between py-3 border-t">
              <span className="text-muted-foreground">Registration Fee</span>
              <span className="text-2xl font-bold text-primary">${teamInfo.per_player_fee}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-t">
              <span className="text-muted-foreground">Players Registered</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{teamInfo.players_paid} / {teamInfo.max_players}</span>
                {spotsRemaining <= 3 && spotsRemaining > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    Only {spotsRemaining} spots left!
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Already Paid Players */}
        {teamInfo.paid_players && teamInfo.paid_players.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Registered Players
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {teamInfo.paid_players.map((name, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    {name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registration Form */}
        {spotsRemaining > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Player Registration</CardTitle>
              <CardDescription>
                Enter player details to complete registration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="player_name">Player Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="player_name"
                      name="player_name"
                      value={formData.player_name}
                      onChange={handleChange}
                      placeholder="Full name"
                      className="pl-10"
                      required
                      data-testid="player-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="player_email">Player/Parent Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="player_email"
                      name="player_email"
                      type="email"
                      value={formData.player_email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      className="pl-10"
                      required
                      data-testid="player-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jersey_number">Preferred Jersey Number</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="jersey_number"
                      name="jersey_number"
                      type="number"
                      min="0"
                      max="99"
                      value={formData.jersey_number}
                      onChange={handleChange}
                      placeholder="Optional"
                      className="pl-10"
                      data-testid="jersey-number-input"
                    />
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Parent/Guardian Info (Optional)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="parent_name">Parent Name</Label>
                      <Input
                        id="parent_name"
                        name="parent_name"
                        value={formData.parent_name}
                        onChange={handleChange}
                        placeholder="Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="parent_email">Parent Email</Label>
                      <Input
                        id="parent_email"
                        name="parent_email"
                        type="email"
                        value={formData.parent_email}
                        onChange={handleChange}
                        placeholder="Email"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary gap-2"
                  disabled={submitting}
                  data-testid="submit-registration-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {submitting ? 'Processing...' : `Pay $${teamInfo.per_player_fee} & Register`}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Secure payment powered by Stripe. Your information is encrypted and secure.
                </p>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Team Roster Full</h3>
              <p className="text-muted-foreground">
                This team has reached maximum capacity. Contact the coach for more information.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
